import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Plus, Search } from "lucide-react";
import { Main } from "@/components/Main";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { appDialogSecondaryButton } from "@/components/ui/app-dialog";
import {
  exportGameBankGames,
  getGameBankGames,
  getGameBankLotteries,
  GameBankGamesPaginated,
  GameBankLottery,
} from "@/Api/gameBank";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { LotteryBadge, NumberChip, pad2 } from "./shared";
import { ExportMenu, fileSlug } from "./ExportMenu";

const PER_PAGE = 20;

/**
 * Banco de Bolões > Banco de Jogos > Ver jogos.
 * Combinações cadastradas de uma loteria em uma quantidade de dezenas, paginadas.
 */
export const GameBankGamesPage = () => {
  const navigate = useNavigate();
  const { lotteryId, dezenas } = useParams();
  const { hasPermission, isAdmin } = usePermissions();
  const canManage = isAdmin() || hasPermission(PERMISSIONS.MANAGE_GAME_BANK);

  const [lottery, setLottery] = useState<GameBankLottery | null>(null);
  const [data, setData] = useState<GameBankGamesPaginated | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGameBankLotteries().then(({ ok, data: body }) => {
      if (ok && Array.isArray(body?.response)) {
        setLottery(body.response.find((item: GameBankLottery) => item.id === Number(lotteryId)) ?? null);
      }
    });
  }, [lotteryId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data: body } = await getGameBankGames({
        lottery_id: Number(lotteryId),
        dezenas: Number(dezenas),
        page,
        per_page: PER_PAGE,
        search: appliedSearch || undefined,
      });
      if (ok && body?.response) setData(body.response);
    } finally {
      setLoading(false);
    }
  }, [lotteryId, dezenas, page, appliedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const backTo = `/banco-de-boloes?loteria=${lotteryId}`;

  return (
    <Main>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white">{pad2(Number(dezenas))} dezenas</h1>
            {lottery && <LotteryBadge name={lottery.name} color={lottery.color} />}
          </div>
          <p className="text-sm text-muted-foreground">
            {data
              ? `${data.total.toLocaleString("pt-BR")} ${data.total === 1 ? "combinação cadastrada" : "combinações cadastradas"}`
              : "Carregando..."}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ExportMenu
            disabled={!data?.total}
            fileName={fileSlug(`banco de jogos ${lottery?.name ?? "loteria"} ${pad2(Number(dezenas))} dezenas`)}
            request={(format) => exportGameBankGames({ lottery_id: Number(lotteryId), dezenas: Number(dezenas) }, format)}
          />
          {canManage && (
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600"
              onClick={() => navigate(`/banco-de-boloes/${lotteryId}/planilhas/nova?dezenas=${dezenas}`)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Cadastrar jogos
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4 sm:p-6">
          <form
            className="flex gap-2 sm:w-80"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setAppliedSearch(search);
            }}
          >
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar pelo Nº do jogo"
              inputMode="numeric"
            />
            <Button type="submit" className={appDialogSecondaryButton} aria-label="Buscar">
              <Search className="h-4 w-4" />
            </Button>
          </form>

          {loading && !data ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : !data || data.data.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              Nenhum jogo encontrado.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <div className="hidden grid-cols-[4rem_1fr] gap-4 bg-muted/60 px-4 py-3 text-sm font-semibold sm:grid">
                <span>Nº</span>
                <span>Combinação cadastrada</span>
              </div>
              {data.data.map((game, index) => (
                <div
                  key={game.id}
                  className={`flex flex-col gap-2 border-t px-4 py-3 sm:grid sm:grid-cols-[4rem_1fr] sm:items-center sm:gap-4 ${
                    index % 2 === 0 ? "bg-violet-50/60 dark:bg-violet-950/20" : ""
                  }`}
                >
                  <span className="font-semibold">
                    <span className="text-xs font-normal text-muted-foreground sm:hidden">Nº </span>
                    {game.game_number}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {game.numbers.map((number, i) => (
                      <NumberChip key={`${game.id}-${i}`} value={number} color={lottery?.color} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {data && data.total > 0 && (
            <div className="flex flex-col items-center justify-between gap-3 text-sm sm:flex-row">
              <span className="text-muted-foreground">
                Exibindo {data.from}–{data.to} de {data.total.toLocaleString("pt-BR")}{" "}
                {data.total === 1 ? "combinação" : "combinações"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className={appDialogSecondaryButton}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => current - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>
                  Página {data.current_page} de {data.last_page}
                </span>
                <Button
                  size="sm"
                  className={appDialogSecondaryButton}
                  disabled={page >= data.last_page || loading}
                  onClick={() => setPage((current) => current + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Main>
  );
};
